import { makeAutoObservable, runInAction } from 'mobx';
import { screenService } from '../services/screenService';
import type { WaitingScreen, ScreenUploadRequest, LLMTokenUsageResponse } from '../types';

class ScreenStore {
  screens: WaitingScreen[] = [];
  currentScreen: WaitingScreen | null = null;
  statistics = {
    totalCount: 0,
    latestScreenTime: undefined as string | undefined,
  } as { totalCount: number; latestScreenTime?: string };
  isLoading = false;
  isUploading = false;
  error: string | null = null;
  dateFilterError: string | null = null;
  // Individual loading states for statistics
  loadingStates = {
    totalScreens: false,
    latestScreenTime: false,
  };
  llmTokenUsage: LLMTokenUsageResponse | null = null;
  llmTokenUsageLoading = false;
  pagination = {
    page: 1,
    limit: 50,
    totalItems: 0,
    totalPages: 0,
  };
  filters = {
    fromDate: '',
    toDate: '',
    fromTime: '',
    toTime: '',
  };
  nextToken: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private buildDateTime(date: string, time: string, isEndOfDay: boolean): string | undefined {
    const today = new Date().toISOString().split('T')[0];

    // 日付も時刻もない場合
    if (!date && !time) {
      return undefined;
    }

    // 日付のみ
    if (date && !time) {
      if (isEndOfDay) {
        return `${date}T23:59`;
      } else {
        return `${date}T00:00`;
      }
    }

    // 時刻のみ（本日の指定時刻）
    if (!date && time) {
      return `${today}T${time}`;
    }

    // 両方指定
    return `${date}T${time}`;
  }

  private validateDateFilter(): boolean {
    this.dateFilterError = null;

    // 開始日時と終了日時を組み立て
    const fromDateTime = this.buildDateTime(this.filters.fromDate, this.filters.fromTime, false);
    const toDateTime = this.buildDateTime(this.filters.toDate, this.filters.toTime, true);

    // 両方入力されている場合のみバリデーション
    if (fromDateTime && toDateTime) {
      const from = new Date(fromDateTime);
      const to = new Date(toDateTime);

      if (from > to) {
        this.dateFilterError = '終了日時は開始日時以降を指定してください';
        return false;
      }
    }

    return true;
  }

  async fetchScreens() {
    // 日付フィルタのバリデーション
    if (!this.validateDateFilter()) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // 日時を組み立て
      const fromDateTime = this.buildDateTime(this.filters.fromDate, this.filters.fromTime, false);
      const toDateTime = this.buildDateTime(this.filters.toDate, this.filters.toTime, true);

      const response = await screenService.getScreens({
        page: this.pagination.page,
        limit: this.pagination.limit,
        fromDate: fromDateTime,
        toDate: toDateTime,
      });

      runInAction(() => {
        this.screens = response.items;
        this.pagination = {
          page: response.page,
          limit: response.limit,
          totalItems: response.totalItems,
          totalPages: response.totalPages,
        };
        this.nextToken = response.nextToken || null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '画面履歴の取得に失敗しました';
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchMoreScreens() {
    if (!this.nextToken || this.isLoading) {
      return;
    }

    // 日付フィルタのバリデーション
    if (!this.validateDateFilter()) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // 日時を組み立て
      const fromDateTime = this.buildDateTime(this.filters.fromDate, this.filters.fromTime, false);
      const toDateTime = this.buildDateTime(this.filters.toDate, this.filters.toTime, true);

      const response = await screenService.getScreens({
        page: this.pagination.page,
        limit: this.pagination.limit,
        nextToken: this.nextToken,
        fromDate: fromDateTime,
        toDate: toDateTime,
      });

      runInAction(() => {
        // 既存の画面に追加
        this.screens = [...this.screens, ...response.items];
        this.pagination = {
          page: response.page,
          limit: response.limit,
          totalItems: response.totalItems,
          totalPages: response.totalPages,
        };
        this.nextToken = response.nextToken || null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '画面履歴の取得に失敗しました';
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchStatistics() {
    try {
      const stats = await screenService.getStatistics();
      runInAction(() => {
        this.statistics = stats;
      });
    } catch (error: any) {
      console.error('Failed to fetch screen statistics:', error);
    }
  }

  // Individual statistics fetch methods
  async fetchTotalScreensCount() {
    runInAction(() => {
      this.loadingStates.totalScreens = true;
    });

    try {
      const result = await screenService.getTotalScreensCount();
      runInAction(() => {
        this.statistics.totalCount = result.count;
        this.loadingStates.totalScreens = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadingStates.totalScreens = false;
      });
      console.error('Failed to fetch total screens count:', error);
    }
  }

  async fetchLatestScreenTime() {
    runInAction(() => {
      this.loadingStates.latestScreenTime = true;
    });

    try {
      const result = await screenService.getLatestScreenTime();
      runInAction(() => {
        this.statistics.latestScreenTime = result.time || undefined;
        this.loadingStates.latestScreenTime = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadingStates.latestScreenTime = false;
      });
      console.error('Failed to fetch latest screen time:', error);
    }
  }

  async fetchLLMTokenUsage() {
    runInAction(() => {
      this.llmTokenUsageLoading = true;
    });
    try {
      const result = await screenService.getLLMTokenUsage();
      runInAction(() => {
        this.llmTokenUsage = result;
        this.llmTokenUsageLoading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.llmTokenUsageLoading = false;
      });
      console.error('Failed to fetch LLM token usage:', error);
    }
  }

  async uploadScreen(request: ScreenUploadRequest) {
    this.isUploading = true;
    this.error = null;
    
    try {
      const response = await screenService.uploadScreen(request);
      
      if (response.success && response.data) {
        runInAction(() => {
          this.screens.unshift(response.data!);
        });
      }
      
      return response.success;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '画像のアップロードに失敗しました';
      });
      return false;
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  }

  async deleteScreen(id: string) {
    try {
      await screenService.deleteScreen(id);
      runInAction(() => {
        this.screens = this.screens.filter(s => s.id !== id);
      });
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '画像の削除に失敗しました';
      });
      return false;
    }
  }

  setPage(page: number) {
    this.pagination.page = page;
    this.fetchScreens();
  }

  setFilter(key: keyof typeof this.filters, value: string) {
    this.filters[key] = value;
    this.pagination.page = 1;
    this.nextToken = null;
    this.fetchScreens();
  }

  clearFilters() {
    this.filters = {
      fromDate: '',
      toDate: '',
      fromTime: '',
      toTime: '',
    };
    this.pagination.page = 1;
    this.nextToken = null;
    this.dateFilterError = null;
    this.fetchScreens();
  }

  clearError() {
    this.error = null;
  }

  clearDateFilterError() {
    this.dateFilterError = null;
  }

  get latestScreen() {
    return this.screens.length > 0 ? this.screens[0] : null;
  }

  get totalScreensCount() {
    return this.statistics.totalCount;
  }

  get latestScreenTime() {
    return this.statistics.latestScreenTime;
  }

  get hasMore() {
    return this.nextToken !== null;
  }
}

export const screenStore = new ScreenStore();